import { useState } from 'react';
import {
  Box,
  Button,
  ButtonGroup,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Menu,
  MenuItem,
  Popover,
  Select,
  Stack,
  Tooltip,
  Typography
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { toast } from 'react-toastify';
import {
  useOpenerState,
  useResetGroupDefaultConfig,
  useSaveGroupDefaultConfig
} from '../../api/useEntry';
import type { OpenerGroup } from '../../api/useGroups';
import {
  ENGINE_TIERS,
  useOpenerGroupPanic,
  useOpenerGroupPanicSoft,
  useOpenerGroupStart,
  useOpenerGroupStop,
  useOpenerGroupsPodState
} from '../../api/useOpenerGroupPods';
import type { EngineTier } from '../../api/useOpenerGroupPods';
import {
  selectableAccounts,
  useVenueAccounts,
  VenuePlatform
} from '../../api/useVenueAccounts';
import { GROUP_ACCOUNT_UNSET, groupAccountPair } from './groupAccounts';
import { FinishGroupButton } from './FinishGroupButton';
import { HEADER_CONTROL_BTN_SX } from '../shared/headerButtonSx';
import {
  GROUP_POD_STATUS_CHIP_COLORS,
  GROUP_POD_STATUS_LABELS,
  canPanicGroup,
  canStartGroup,
  canStopGroup,
  deriveCapacityIndicator,
  deriveGroupPodStatus
} from './groupPodStatus';

type ConfirmAction = 'stop' | 'panic-soft' | 'panic' | 'reset-default';

/**
 * Per-group pod controls — rendered on the Entry page ONLY while a
 * specific group is selected. Start/Stop drive the group's dedicated
 * engine + feed pods via prediction-manager; Soft Panic cancels + pauses
 * the group's rules (engine pod stays); Hard Panic kills the group engine
 * pod and cancels ALL the group's orders externally. Mirrors the
 * StopStartOpenerButtons conventions (compact chip + outlined buttons,
 * warning = soft, error = hard).
 */
export const GroupPodControls = ({
  groupId,
  groupName,
  group
}: {
  groupId: string;
  groupName?: string | null;
  /** Active group object — supplies `defaultConfigSavedAt`/`pairCount` for the
   *  Save/Reset default controls and the stored venue accounts the Accounts
   *  popover starts from. Optional so callers without it still render the pod
   *  controls. */
  group?: Pick<
    OpenerGroup,
    | 'defaultConfigSavedAt'
    | 'pairCount'
    | 'kalshiAccountId'
    | 'polymarketAccountId'
  > | null;
}) => {
  const { data: podState } = useOpenerGroupsPodState();
  const { data: openerState } = useOpenerState();
  const startMutation = useOpenerGroupStart();
  const stopMutation = useOpenerGroupStop();
  const panicSoftMutation = useOpenerGroupPanicSoft();
  const panicMutation = useOpenerGroupPanic();
  const saveDefaultMutation = useSaveGroupDefaultConfig();
  const resetDefaultMutation = useResetGroupDefaultConfig();

  const groupState = podState?.find(g => g.groupId === groupId);
  const status = deriveGroupPodStatus(groupState);
  const capacity =
    groupState?.expectedUnits != null &&
    groupState.ceilingUnits != null &&
    groupState.ceilingUnits > 0
      ? deriveCapacityIndicator(
          groupState.expectedUnits,
          groupState.ceilingUnits
        )
      : null;
  const lockout = openerState?.panicLockout;
  const lockoutArmed = Boolean(lockout?.armed);
  const isBusy =
    startMutation.isLoading ||
    stopMutation.isLoading ||
    panicSoftMutation.isLoading ||
    panicMutation.isLoading ||
    saveDefaultMutation.isLoading ||
    resetDefaultMutation.isLoading;

  const [confirm, setConfirm] = useState<ConfirmAction | null>(null);
  const [startMenuAnchor, setStartMenuAnchor] = useState<null | HTMLElement>(
    null
  );
  // Operator tier override for the next start. undefined = Auto (let the
  // engine size itself by pair count). Applies to whichever start action fires.
  const [startTier, setStartTier] = useState<EngineTier | undefined>(undefined);
  // Start-time account override, for THIS run only. Prefilled with the group's
  // stored pair every time the popover opens, so the operator edits from what
  // the group actually trades rather than from an empty pair.
  const [accountsAnchor, setAccountsAnchor] = useState<null | HTMLElement>(null);
  const [overrideKalshi, setOverrideKalshi] = useState('');
  const [overridePolymarket, setOverridePolymarket] = useState('');
  const {
    data: venueAccounts,
    isLoading: accountsLoading,
    isError: accountsErrored
  } = useVenueAccounts(Boolean(accountsAnchor));
  // Keyed on the DATA, not on the status flag alone: react-query's `isError`
  // is sticky and RETAINS the last successful `data`, so a failed background
  // refetch would otherwise claim the registry is unreadable while the select
  // is fully populated from the earlier success.
  const accountsUnreachable = accountsErrored && !venueAccounts;

  const runningTier = groupState?.observedTier ?? groupState?.recommendedTier;

  // Straight off the GROUP, never off the registry read: which accounts a group
  // trades on is a property of the group, so the chip keeps telling the truth
  // while `useVenueAccounts` is loading or unreachable.
  const accountPair = groupAccountPair(group);

  const label = groupName || groupState?.name || 'this group';

  const startDisabled = isBusy || !canStartGroup(status, lockoutArmed);

  const defaultSavedAt = group?.defaultConfigSavedAt ?? null;
  const hasDefault = Boolean(defaultSavedAt);

  const handleSaveDefault = () => {
    saveDefaultMutation.mutate(groupId, {
      onSuccess: result => {
        toast(
          `Saved default config for "${label}" (${result.ruleCount} rule${
            result.ruleCount === 1 ? '' : 's'
          }).`,
          { theme: 'dark', type: 'success', autoClose: 4000 }
        );
      }
    });
  };

  const confirmCopy: Record<
    ConfirmAction,
    { title: string; body: string; cta: string; color: 'warning' | 'error' }
  > = {
    stop: {
      title: `Stop group — ${label}`,
      body:
        `Stops the pods of "${label}": pauses the group's rules, cancels ` +
        'their resting orders during the engine drain, then removes the ' +
        "group's engine + feed pods. Other groups and the residual opener " +
        'are NOT touched. No lockout — the group can be started again.',
      cta: 'Stop group',
      color: 'warning'
    },
    'panic-soft': {
      title: `Soft panic — ${label}`,
      body:
        `Cancels all open orders of "${label}" and pauses its rules ` +
        '(group-scoped PANIC 24H). The group engine pod STAYS up. Other ' +
        'groups are NOT touched. No lockout.',
      cta: 'Soft panic group',
      color: 'warning'
    },
    panic: {
      title: `HARD panic — ${label}`,
      body:
        `KILLS the engine pod of "${label}" immediately and cancels ALL ` +
        "the group's orders externally on Kalshi + Polymarket, then pauses " +
        'its rules. Other groups are NOT touched. No lockout — the group ' +
        'can be restarted right away.',
      cta: 'Hard panic group',
      color: 'error'
    },
    'reset-default': {
      title: `Reset to default — ${label}`,
      body:
        `Restores each rule in "${label}" to its OWN saved config (applied ` +
        'live to running rules; rules added after the last Save are left ' +
        'untouched). CONFIG ONLY — this will NOT enable or disable anything ' +
        '(maker flags and platform on/off are untouched).',
      cta: 'Reset to default',
      color: 'warning'
    }
  };

  const openAccounts = (anchor: HTMLElement) => {
    setOverrideKalshi(group?.kalshiAccountId ?? '');
    setOverridePolymarket(group?.polymarketAccountId ?? '');
    setAccountsAnchor(anchor);
  };

  const overrideAccountSelect = (
    platform: VenuePlatform,
    label: string,
    value: string,
    onPick: (next: string) => void
  ) => {
    const options = selectableAccounts(venueAccounts, platform);
    // Neither while the registry is still loading NOR when the read FAILED:
    // the options list is empty in both cases, and calling the group's OWN
    // account "disabled or unknown" on a control that arms real money is the
    // wrong thing to say when we are still asking, or never got an answer.
    const savedButUnofferable =
      !accountsLoading &&
      !accountsUnreachable &&
      !!value &&
      !options.some(a => a.accountId === value);
    return (
      <Select
        size="small"
        value={value}
        displayEmpty
        onChange={e => onPick(e.target.value as string)}
        sx={{ fontSize: '0.75rem', minWidth: 200, height: 30 }}
        inputProps={{ 'aria-label': label }}
        renderValue={v =>
          v
            ? (options.find(a => a.accountId === v)?.name ?? (v as string))
            : `${label} — none set`
        }
      >
        {accountsLoading && !!value && (
          <MenuItem disabled value={value} sx={{ fontSize: '0.75rem' }}>
            {value} (loading…)
          </MenuItem>
        )}
        {accountsUnreachable && !!value && (
          <MenuItem disabled value={value} sx={{ fontSize: '0.75rem' }}>
            {value} (account registry unreachable)
          </MenuItem>
        )}
        {savedButUnofferable && (
          <MenuItem disabled value={value} sx={{ fontSize: '0.75rem' }}>
            {value} (not selectable — disabled or unknown)
          </MenuItem>
        )}
        {options.map(a => (
          <MenuItem key={a.accountId} value={a.accountId} sx={{ fontSize: '0.75rem' }}>
            {a.name} — {a.accountId}
          </MenuItem>
        ))}
      </Select>
    );
  };

  const runConfirmed = () => {
    if (confirm === 'stop') stopMutation.mutate(groupId);
    else if (confirm === 'panic-soft') panicSoftMutation.mutate(groupId);
    else if (confirm === 'panic') panicMutation.mutate(groupId);
    else if (confirm === 'reset-default') resetDefaultMutation.mutate(groupId);
    setConfirm(null);
  };

  return (
    <>
      <Stack direction="row" spacing={0.4} alignItems="center">
        {accountPair && (
          <Tooltip
            title={
              accountPair.incomplete
                ? 'This group is missing a venue account — the opener refuses to start a group whose accounts are unset. Pick both under Accounts.'
                : `Venue accounts this group trades on: Kalshi ${
                    group?.kalshiAccountId ?? GROUP_ACCOUNT_UNSET
                  }, Polymarket ${
                    group?.polymarketAccountId ?? GROUP_ACCOUNT_UNSET
                  }. Both are REAL-MONEY accounts — a "test" account is a different account at the venue, not a simulation.`
            }
            placement="top"
          >
            <Chip
              size="small"
              label={accountPair.label}
              color={accountPair.incomplete ? 'warning' : 'default'}
              variant="outlined"
              sx={{ fontSize: '0.65rem', height: 20, mr: 0.25 }}
            />
          </Tooltip>
        )}
        <Tooltip
          title={
            status === 'provisioning'
              ? 'Group pods desired ON — waiting for the engine pod to become Ready'
              : status === 'running'
                ? `Group engine + feed pods running${
                    typeof groupState?.engineReplicas === 'number'
                      ? ` (${groupState.engineReplicas} engine replica${
                          groupState.engineReplicas === 1 ? '' : 's'
                        })`
                      : ''
                  }`
                : status === 'panicked'
                  ? 'Group hard-panicked — engine pod killed, orders cancelled. Start brings it back.'
                  : 'Group pods are off — rules in this group run nowhere until started'
          }
          placement="top"
        >
          <Chip
            size="small"
            label={`Pods: ${GROUP_POD_STATUS_LABELS[status]}`}
            color={GROUP_POD_STATUS_CHIP_COLORS[status]}
            variant={status === 'off' ? 'outlined' : 'filled'}
            icon={
              status === 'provisioning' ? (
                <CircularProgress size={10} color="inherit" sx={{ ml: 0.5 }} />
              ) : undefined
            }
            sx={{ fontSize: '0.65rem', height: 20, mr: 0.25 }}
          />
        </Tooltip>
        {capacity && (
          <Tooltip
            title={`Engine load: ${capacity.label} leg-units (tokio tasks) vs the running tier's ceiling. Over 100% the engine is above its design ceiling — resize the group (STOP then START) or split it.`}
            placement="top"
          >
            <Chip
              size="small"
              label={capacity.label}
              color={capacity.color}
              variant="outlined"
              sx={{ fontSize: '0.65rem', height: 20, mr: 0.25 }}
            />
          </Tooltip>
        )}
        {runningTier && (
          <Tooltip
            title={`Running engine tier: ${runningTier.toUpperCase()}${
              groupState?.observedTier
                ? ''
                : ' (recommended — auto-sizer estimate)'
            }`}
            placement="top"
          >
            <Chip
              size="small"
              label={`Tier: ${runningTier.toUpperCase()}`}
              color="default"
              variant="outlined"
              sx={{ fontSize: '0.65rem', height: 20, mr: 0.25 }}
            />
          </Tooltip>
        )}
        <Tooltip
          title={
            lockoutArmed
              ? `Global opener panic lockout armed${
                  lockout?.expiresAt
                    ? ` until ${new Date(lockout.expiresAt).toLocaleString()}`
                    : ''
                } — group start is refused while it holds`
              : status === 'running' || status === 'provisioning'
                ? 'Group pods already on'
                : `Start dedicated engine + feed pods for "${label}"`
          }
          placement="top"
        >
          <span>
            <ButtonGroup variant="outlined" color="success" size="small">
              <Button
                startIcon={
                  startMutation.isLoading ? (
                    <CircularProgress size={12} color="inherit" />
                  ) : (
                    <PlayArrowIcon />
                  )
                }
                onClick={() => {
                  // Plain Start runs on the group's STORED pair — it sends no
                  // account, so it can never quietly move a group's money.
                  startMutation.mutate(
                    { groupId, tier: startTier },
                    { onSuccess: () => setStartTier(undefined) }
                  );
                }}
                disabled={startDisabled}
                sx={HEADER_CONTROL_BTN_SX}
              >
                {startTier ? `Start ${startTier.toUpperCase()}` : 'Start group'}
              </Button>
              <Button
                size="small"
                disabled={startDisabled}
                onClick={e => setStartMenuAnchor(e.currentTarget)}
                sx={{ px: 0.25, minWidth: 0 }}
                aria-label="Start options"
              >
                <ArrowDropDownIcon fontSize="small" />
              </Button>
            </ButtonGroup>
          </span>
        </Tooltip>
        <Menu
          anchorEl={startMenuAnchor}
          open={Boolean(startMenuAnchor)}
          onClose={() => setStartMenuAnchor(null)}
        >
          <Box sx={{ px: 2, py: 1, maxWidth: 280 }}>
            <Typography sx={{ fontSize: '0.7rem', color: 'grey.500', mb: 0.5 }}>
              Engine tier — override the pod size for whichever start you pick.
              Leave on Auto to size by pair count.
            </Typography>
            <Select
              size="small"
              value={startTier ?? ''}
              displayEmpty
              onChange={e =>
                setStartTier((e.target.value || undefined) as
                  | EngineTier
                  | undefined)
              }
              sx={{ fontSize: '0.75rem', minWidth: 120, height: 28 }}
              inputProps={{ 'aria-label': 'Engine tier override' }}
              renderValue={v =>
                v ? (v as string).toUpperCase() : 'Auto'
              }
            >
              <MenuItem value="" sx={{ fontSize: '0.75rem' }}>
                Auto
              </MenuItem>
              {ENGINE_TIERS.map(t => (
                <MenuItem key={t} value={t} sx={{ fontSize: '0.75rem' }}>
                  {t.toUpperCase()}
                </MenuItem>
              ))}
            </Select>
          </Box>
          <Divider />
          <MenuItem
            onClick={() => {
              startMutation.mutate(
                { groupId, liveMode: true, tier: startTier },
                { onSuccess: () => setStartTier(undefined) }
              );
              setStartMenuAnchor(null);
            }}
            sx={{ alignItems: 'flex-start', maxWidth: 280 }}
          >
            <Box>
              <Typography
                sx={{ fontSize: '0.8rem', color: 'success.main', fontWeight: 600 }}
              >
                Start in live mode
              </Typography>
              <Typography sx={{ fontSize: '0.7rem', color: 'grey.500' }}>
                Bumps the engine pod ONE tier above its pair-count size for a
                high-load live event. Use for big games; otherwise the engine is
                sized by pair count. A tier override above wins over this bump.
              </Typography>
            </Box>
          </MenuItem>
          <MenuItem
            onClick={e => {
              // Anchored to the caret, not to this item: the item unmounts with
              // the menu, and a Popover whose anchor is gone jumps to the corner
              // of the screen.
              const anchor = startMenuAnchor ?? (e.currentTarget as HTMLElement);
              setStartMenuAnchor(null);
              openAccounts(anchor);
            }}
            sx={{ alignItems: 'flex-start', maxWidth: 280 }}
          >
            <Box>
              <Typography
                sx={{ fontSize: '0.8rem', color: 'warning.main', fontWeight: 600 }}
              >
                Accounts…
              </Typography>
              <Typography sx={{ fontSize: '0.7rem', color: 'grey.500' }}>
                Start this run on a DIFFERENT venue account pair. Plain Start
                uses the pair stored on the group.
              </Typography>
            </Box>
          </MenuItem>
        </Menu>
        <Popover
          anchorEl={accountsAnchor}
          open={Boolean(accountsAnchor)}
          onClose={() => setAccountsAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
          <Stack spacing={1} sx={{ px: 2, py: 1.5, maxWidth: 320 }}>
            <Typography sx={{ fontSize: '0.7rem', color: 'grey.500' }}>
              Accounts for THIS run of &quot;{label}&quot;. Prefilled with the
              pair stored on the group; the two venues are independent. The
              override is honored on a fresh start and on a panicked recovery —
              never while the group is already on.
            </Typography>
            {accountsUnreachable && (
              // Said once for the whole popover, not per select: a group with
              // NO stored pair has no value to hang the per-item note on, and
              // two blank selects read as "this build has no accounts
              // registered" — the wrong place to go looking.
              <Typography sx={{ fontSize: '0.7rem', color: 'warning.main' }}>
                Could not read the account registry — prediction-manager may be
                down. These are not empty because no accounts exist.
              </Typography>
            )}
            {overrideAccountSelect(
              'kalshi',
              'Kalshi account',
              overrideKalshi,
              setOverrideKalshi
            )}
            {overrideAccountSelect(
              'polymarket',
              'Polymarket account',
              overridePolymarket,
              setOverridePolymarket
            )}
            <Button
              variant="contained"
              color="success"
              size="small"
              disabled={
                startDisabled || !overrideKalshi || !overridePolymarket
              }
              onClick={() => {
                startMutation.mutate(
                  {
                    groupId,
                    accounts: {
                      kalshiAccountId: overrideKalshi,
                      polymarketAccountId: overridePolymarket
                    },
                    tier: startTier
                  },
                  { onSuccess: () => setStartTier(undefined) }
                );
                setAccountsAnchor(null);
              }}
              sx={HEADER_CONTROL_BTN_SX}
            >
              Start on these accounts
            </Button>
          </Stack>
        </Popover>
        <Tooltip
          title={
            status === 'off'
              ? 'Group pods already off'
              : `Stop "${label}" — pause its rules + drain and remove its pods`
          }
          placement="top"
        >
          <span>
            <Button
              variant="outlined"
              color="warning"
              size="small"
              startIcon={
                stopMutation.isLoading ? (
                  <CircularProgress size={12} color="inherit" />
                ) : (
                  <PowerSettingsNewIcon />
                )
              }
              onClick={() => setConfirm('stop')}
              disabled={isBusy || !canStopGroup(status)}
              sx={HEADER_CONTROL_BTN_SX}
            >
              Stop group
            </Button>
          </span>
        </Tooltip>
        <Tooltip
          title={`Cancel "${label}"'s open orders + pause its rules. Engine pod stays up.`}
          placement="top"
        >
          <span>
            <Button
              variant="contained"
              color="warning"
              size="small"
              startIcon={
                panicSoftMutation.isLoading ? (
                  <CircularProgress size={12} color="inherit" />
                ) : (
                  <WarningAmberIcon />
                )
              }
              onClick={() => setConfirm('panic-soft')}
              disabled={isBusy || !canPanicGroup(status)}
              sx={HEADER_CONTROL_BTN_SX}
            >
              Soft panic
            </Button>
          </span>
        </Tooltip>
        <Tooltip
          title={`Kill "${label}"'s engine pod + cancel ALL its orders externally.`}
          placement="top"
        >
          <span>
            <Button
              variant="contained"
              color="error"
              size="small"
              startIcon={
                panicMutation.isLoading ? (
                  <CircularProgress size={12} color="inherit" />
                ) : (
                  <WarningAmberIcon />
                )
              }
              onClick={() => setConfirm('panic')}
              disabled={isBusy || !canPanicGroup(status)}
              sx={HEADER_CONTROL_BTN_SX}
            >
              Hard panic
            </Button>
          </span>
        </Tooltip>
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
        <FinishGroupButton groupId={groupId} groupName={label} />
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
        <Tooltip
          title={
            hasDefault && defaultSavedAt
              ? `Snapshot each rule's current config as its own default (overwrites the one saved ${new Date(
                  defaultSavedAt
                ).toLocaleString()})`
              : "Snapshot each rule's current config as its own default"
          }
          placement="top"
        >
          <span>
            <Button
              variant="outlined"
              color="info"
              size="small"
              startIcon={
                saveDefaultMutation.isLoading ? (
                  <CircularProgress size={12} color="inherit" />
                ) : (
                  <SaveOutlinedIcon />
                )
              }
              onClick={handleSaveDefault}
              disabled={isBusy}
              sx={HEADER_CONTROL_BTN_SX}
            >
              Save default
            </Button>
          </span>
        </Tooltip>
        <Tooltip
          title={
            hasDefault
              ? `Restore each rule in "${label}" to its own saved config (config only)`
              : 'No default saved yet — press "Save default" first'
          }
          placement="top"
        >
          <span>
            <Button
              variant="outlined"
              color="warning"
              size="small"
              startIcon={
                resetDefaultMutation.isLoading ? (
                  <CircularProgress size={12} color="inherit" />
                ) : (
                  <RestartAltIcon />
                )
              }
              onClick={() => setConfirm('reset-default')}
              disabled={isBusy || !hasDefault}
              sx={HEADER_CONTROL_BTN_SX}
            >
              Reset to default
            </Button>
          </span>
        </Tooltip>
      </Stack>

      <Dialog
        open={confirm !== null}
        onClose={() => setConfirm(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: '1rem' }}>
          {confirm ? confirmCopy[confirm].title : ''}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '0.85rem' }}>
            {confirm ? confirmCopy[confirm].body : ''}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button size="small" onClick={() => setConfirm(null)}>
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            color={confirm ? confirmCopy[confirm].color : 'warning'}
            onClick={runConfirmed}
          >
            {confirm ? confirmCopy[confirm].cta : ''}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
